import { useEffect, useState, useMemo } from "react";
import api, { API, formatApiErrorDetail } from "../../lib/api";
import { Button } from "../../components/ui/button";
import { Card } from "../../components/ui/card";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Switch } from "../../components/ui/switch";
import { Textarea } from "../../components/ui/textarea";
import { Checkbox } from "../../components/ui/checkbox";
import {
  Tabs,
  TabsList,
  TabsTrigger,
  TabsContent,
} from "../../components/ui/tabs";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../../components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "../../components/ui/dialog";
import { useAuth } from "../../context/AuthContext";
import { Save, Shield, History, Upload, Award, GraduationCap, RotateCcw, Tag, Plus, Trash2, Edit, Package, ExternalLink, Info, Mail, Send, Receipt, Image as ImageIcon, BookOpen, Eye, EyeOff, Clock, Users as UsersIcon, FlaskConical, Loader2 } from "lucide-react";
import MarkdownEditor, { MarkdownView } from "../../components/MarkdownEditor";
import { toast } from "sonner";
import DocumentSettingsPanel from "./DocumentSettingsPanel";

const EMPTY_PROMO = {
  name: "",
  enabled: true,
  kind: "bulk",
  starts_at: "",
  ends_at: "",
  bulk_percent: 10,
  bulk_min_courses: 2,
  course_id: "",
  course_discount_type: "percent",
  course_discount_value: 10,
  course_ids: [],
  fixed_price: 0,
  quantity_count: 2,
  applies_to: "both",
};

// What a promotion is allowed to touch. "final" courses are Cursos (diploma),
// "normal" ones are Talleres (certificado).
const APPLIES_TO_LABELS = {
  both: "Cursos y talleres",
  courses: "Solo cursos",
  workshops: "Solo talleres",
};

// Wording used in the toasts and the reset confirmation of each manual.
const MANUAL_LABELS = {
  admin: "del administrador",
  gestion: "del rol gestión",
  user: "del usuario",
};

export default function AdminSettings() {
  const { user: currentUser } = useAuth();
  const isSuperadmin = currentUser?.role === "superadmin";
  // Test-data cleanup (superadmin only).
  const [testData, setTestData] = useState(null);
  const [testPurging, setTestPurging] = useState(false);
  const [testConfirm, setTestConfirm] = useState(false);
  // Perms: gestion may not see the Promotions section unless the SA turned it
  // on. Superadmin + admin roles always see it.
  const canPromotions =
    currentUser?.role === "superadmin" ||
    currentUser?.role === "admin" ||
    !!(currentUser?.perms || {}).create_promotions;
  // Fiscal identity (issuer data + VAT) is admin/superadmin only — gestión can
  // read it but not change it. Mirrors `require_admin_or_sa` on the backend's
  // PUT /admin/settings/invoice and the logo upload/delete endpoints.
  const canEditInvoiceCfg =
    currentUser?.role === "superadmin" || currentUser?.role === "admin";
  const [policyText, setPolicyText] = useState("");
  const [policyVersion, setPolicyVersion] = useState(null);
  const [policyUpdatedAt, setPolicyUpdatedAt] = useState(null);
  const [policySaving, setPolicySaving] = useState(false);
  const [consents, setConsents] = useState([]);
  const [consentsLoading, setConsentsLoading] = useState(false);
  const [consentsOpen, setConsentsOpen] = useState(false);
  const [promotions, setPromotions] = useState([]);
  const [catalog, setCatalog] = useState([]);
  const [editing, setEditing] = useState(null); // null = closed, otherwise the promo object
  const [savingPromo, setSavingPromo] = useState(false);
  const [homeMoreInfo, setHomeMoreInfo] = useState("");
  const [homeHighlight, setHomeHighlight] = useState("");
  const [homeMoreInfoSaving, setHomeMoreInfoSaving] = useState(false);
  const [emailCfg, setEmailCfg] = useState({
    sender: "",
    reply_to: [],
    events: {
      admin_purchase_notification: { enabled: true, recipients: [] },
      customer_purchase_confirmation: { enabled: true, recipients: [] },
      account_credentials: { enabled: true, recipients: [] },
      manual_ticket_confirmation: { enabled: true, recipients: [] },
      installment_paid_confirmation: { enabled: true, recipients: [] },
      diploma_available_notification: { enabled: true, recipients: [] },
    },
  });
  const [emailSaving, setEmailSaving] = useState(false);
  const [emailTestTo, setEmailTestTo] = useState("");
  const [emailTesting, setEmailTesting] = useState(false);

  // ----- Invoice settings (issuer + VAT + logo) -----
  const [invoiceCfg, setInvoiceCfg] = useState({
    company_name: "",
    cif: "",
    address: "",
    phone: "",
    email: "",
    web: "",
    vat_percent: 0,
  });
  const [invoiceSaving, setInvoiceSaving] = useState(false);
  const [invoiceLogoPresent, setInvoiceLogoPresent] = useState(false);
  const [invoiceLogoVersion, setInvoiceLogoVersion] = useState(0);
  const [invoiceLogoPreview, setInvoiceLogoPreview] = useState(null);
  const [invoiceLogoUploading, setInvoiceLogoUploading] = useState(false);

  // ----- Manuals (editable Markdown for admin + user) -----
  const [manuals, setManuals] = useState({
    admin:   { text: "", custom: false, updated_at: null },
    gestion: { text: "", custom: false, updated_at: null },
    user:    { text: "", custom: false, updated_at: null },
  });
  // gestion never sees the admin manual, so it must not be their default tab.
  const isGestion = currentUser?.role === "gestion";
  const [manualVariant, setManualVariant] = useState("admin"); // "admin" | "gestion" | "user"
  const [manualEdits, setManualEdits] = useState({ admin: "", gestion: "", user: "" });
  const [manualSaving, setManualSaving] = useState(false);
  const [manualResetting, setManualResetting] = useState(false);
  const [manualPreview, setManualPreview] = useState(false);

  // ----- Inactivity reminders -----
  const [inactivityDays, setInactivityDays] = useState(7);
  const [inactiveUsers, setInactiveUsers] = useState([]);
  const [inactiveLoading, setInactiveLoading] = useState(false);
  const [inactiveSending, setInactiveSending] = useState(false);
  const [inactiveSelected, setInactiveSelected] = useState([]);

  const loadPromos = () => api.get("/admin/promotions").then(({ data }) => setPromotions(data));

  useEffect(() => {
    api.get("/admin/settings/policy").then(({ data }) => {
      setPolicyText(data.text || "");
      setPolicyVersion(data.version);
      setPolicyUpdatedAt(data.updated_at);
    });
    loadPromos();
    api.get("/admin/courses").then(({ data }) => setCatalog(data));
    api.get("/admin/settings/home-more-info").then(({ data }) => {
      setHomeMoreInfo(data?.text || "");
      setHomeHighlight(data?.highlight || "");
    });
    api.get("/admin/settings/email").then(({ data }) => setEmailCfg(data));
    api.get("/admin/settings/invoice").then(({ data }) => setInvoiceCfg(data));
    api
      .get("/admin/settings/invoice/logo")
      .then(({ data }) => setInvoiceLogoPresent(!!data?.present))
      .catch(() => setInvoiceLogoPresent(false));
    api
      .get("/admin/settings/manuals")
      .then(({ data }) => {
        // Merged, not replaced: the API omits the manuals this role may not
        // touch, and the card indexes manuals[variant] directly.
        setManuals((prev) => ({ ...prev, ...data }));
        setManualEdits({
          admin: data.admin?.text || "",
          gestion: data.gestion?.text || "",
          user: data.user?.text || "",
        });
      })
      .catch(() => {});
  }, []);

  // Fetches the current invoice logo (or default brand logo) as a blob URL
  // so the <img> can render it even though the endpoint requires auth.
  useEffect(() => {
    const token = localStorage.getItem("token");
    let objectUrl = null;
    fetch(`${API}/admin/settings/invoice/logo/file?v=${invoiceLogoVersion}`, {
      headers: { Authorization: `Bearer ${token}` },
    })
      .then((r) => (r.ok ? r.blob() : null))
      .then((blob) => {
        if (blob) {
          objectUrl = URL.createObjectURL(blob);
          setInvoiceLogoPreview(objectUrl);
        } else {
          setInvoiceLogoPreview(null);
        }
      });
    return () => {
      if (objectUrl) URL.revokeObjectURL(objectUrl);
    };
  }, [invoiceLogoVersion, invoiceLogoPresent]);

  const saveHomeMoreInfo = async () => {
    setHomeMoreInfoSaving(true);
    try {
      const { data } = await api.put("/admin/settings/home-more-info", {
        text: homeMoreInfo,
        highlight: homeHighlight,
      });
      setHomeMoreInfo(data?.text || "");
      setHomeHighlight(data?.highlight || "");
      toast.success("Bloques de la home guardados");
    } catch (err) {
      toast.error(err.response?.data?.detail || "No se pudo guardar");
    } finally {
      setHomeMoreInfoSaving(false);
    }
  };

  const saveEmailCfg = async () => {
    setEmailSaving(true);
    try {
      const { data } = await api.put("/admin/settings/email", emailCfg);
      setEmailCfg(data);
      toast.success("Configuración de emails guardada");
    } catch (err) {
      toast.error(formatApiErrorDetail(err) || "No se pudo guardar");
    } finally {
      setEmailSaving(false);
    }
  };

  const sendTestEmail = async () => {
    if (!emailTestTo.trim()) {
      toast.error("Introduce un email de destino");
      return;
    }
    setEmailTesting(true);
    try {
      await api.post("/admin/settings/email/test", { to: emailTestTo.trim() });
      toast.success(`Enviado a ${emailTestTo.trim()}`);
    } catch (err) {
      toast.error(formatApiErrorDetail(err) || "No se pudo enviar");
    } finally {
      setEmailTesting(false);
    }
  };

  const loadInactiveUsers = async () => {
    setInactiveLoading(true);
    try {
      const { data } = await api.get(`/admin/inactive-users?days=${inactivityDays}`);
      setInactiveUsers(data.users || []);
      setInactiveSelected([]);
    } catch (err) {
      toast.error(formatApiErrorDetail(err) || "No se pudo cargar la lista");
    } finally {
      setInactiveLoading(false);
    }
  };

  const sendInactivityReminders = async () => {
    const targets = inactiveSelected.length > 0 ? inactiveSelected : inactiveUsers.map((u) => u.id);
    if (targets.length === 0) {
      toast.error("No hay alumnos a los que enviar recordatorio");
      return;
    }
    if (!window.confirm(`Se enviará el recordatorio a ${targets.length} alumno${targets.length !== 1 ? "s" : ""}. ¿Continuar?`)) return;
    setInactiveSending(true);
    try {
      const { data } = await api.post("/admin/inactive-users/notify", {
        user_ids: targets,
        days: inactivityDays,
      });
      toast.success(`Enviados: ${data.sent}${data.skipped ? ` · Saltados: ${data.skipped}` : ""}`);
      if (data.errors?.length) {
        toast.error(`${data.errors.length} envíos con error — revisa la consola`);
        console.warn("Inactivity reminder errors:", data.errors);
      }
    } catch (err) {
      toast.error(formatApiErrorDetail(err) || "No se pudo enviar");
    } finally {
      setInactiveSending(false);
    }
  };

  const toggleInactiveSelected = (id) => {
    setInactiveSelected((sel) => (sel.includes(id) ? sel.filter((x) => x !== id) : [...sel, id]));
  };

  const saveInvoiceCfg = async () => {
    if (!invoiceCfg.company_name?.trim()) {
      toast.error("El nombre de la empresa es obligatorio");
      return;
    }
    setInvoiceSaving(true);
    try {
      const { data } = await api.put("/admin/settings/invoice", {
        company_name: invoiceCfg.company_name?.trim() || "",
        cif: invoiceCfg.cif?.trim() || "",
        address: invoiceCfg.address?.trim() || "",
        phone: invoiceCfg.phone?.trim() || "",
        email: invoiceCfg.email?.trim() || "",
        web: invoiceCfg.web?.trim() || "",
        vat_percent: Number(invoiceCfg.vat_percent) || 0,
      });
      setInvoiceCfg(data);
      toast.success("Datos de facturación guardados");
    } catch (err) {
      toast.error(formatApiErrorDetail(err) || "No se pudo guardar");
    } finally {
      setInvoiceSaving(false);
    }
  };

  const uploadInvoiceLogo = async (file) => {
    if (!file) return;
    if (!/\.(png|jpe?g)$/i.test(file.name)) {
      toast.error("Solo PNG o JPG");
      return;
    }
    if (file.size > 5 * 1024 * 1024) {
      toast.error("La imagen excede 5 MB");
      return;
    }
    setInvoiceLogoUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      await api.post("/admin/settings/invoice/logo", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setInvoiceLogoPresent(true);
      setInvoiceLogoVersion((v) => v + 1);
      toast.success("Logo actualizado");
    } catch (err) {
      toast.error(formatApiErrorDetail(err) || "No se pudo subir");
    } finally {
      setInvoiceLogoUploading(false);
    }
  };

  const deleteInvoiceLogo = async () => {
    if (!window.confirm("¿Eliminar el logo actual? Las facturas volverán a usar el logo de la marca por defecto.")) return;
    try {
      await api.delete("/admin/settings/invoice/logo");
      setInvoiceLogoPresent(false);
      setInvoiceLogoVersion((v) => v + 1);
      toast.success("Logo eliminado");
    } catch (err) {
      toast.error(formatApiErrorDetail(err) || "No se pudo eliminar");
    }
  };

  useEffect(() => {
    if (isGestion && manualVariant === "admin") setManualVariant("gestion");
  }, [isGestion, manualVariant]);

  // Its own effect keyed on the role: the session may not be resolved yet on
  // the first render, and the summary is superadmin-only (403 for anyone else).
  useEffect(() => {
    if (currentUser?.role !== "superadmin") return;
    api.get("/admin/test-data/summary").then(({ data }) => setTestData(data)).catch(() => {});
  }, [currentUser?.role]);

  const reloadTestData = async () => {
    try {
      const { data } = await api.get("/admin/test-data/summary");
      setTestData(data);
    } catch { /* nothing to show if it fails */ }
  };

  const purgeTestData = async () => {
    setTestPurging(true);
    try {
      const { data } = await api.post("/admin/test-data/purge");
      toast.success(
        data.total > 0
          ? `Datos de prueba borrados (${data.total} registros)`
          : "No había nada que borrar",
      );
      setTestConfirm(false);
      await reloadTestData();
    } catch (err) {
      toast.error(formatApiErrorDetail(err) || "No se pudieron borrar");
    } finally {
      setTestPurging(false);
    }
  };

  const saveManual = async () => {
    setManualSaving(true);
    try {
      const text = manualEdits[manualVariant] || "";
      if (!text.trim()) { toast.error("El manual no puede estar vacío"); return; }
      const { data } = await api.put("/admin/settings/manuals", {
        variant: manualVariant,
        text,
      });
      setManuals((m) => ({
        ...m,
        [manualVariant]: { text, custom: !!data.custom, updated_at: data.updated_at },
      }));
      toast.success(`Manual ${MANUAL_LABELS[manualVariant]} guardado`);
    } catch (err) {
      toast.error(formatApiErrorDetail(err) || "No se pudo guardar");
    } finally {
      setManualSaving(false);
    }
  };

  const resetManual = async () => {
    const label = MANUAL_LABELS[manualVariant];
    if (!window.confirm(`¿Restaurar el manual ${label} al texto por defecto del sistema? Perderás los cambios personalizados.`)) return;
    setManualResetting(true);
    try {
      const { data } = await api.delete(`/admin/settings/manuals/${manualVariant}`);
      setManuals((m) => ({
        ...m,
        [manualVariant]: { text: data.text || "", custom: false, updated_at: null },
      }));
      setManualEdits((e) => ({ ...e, [manualVariant]: data.text || "" }));
      toast.success("Manual restaurado al texto por defecto");
    } catch (err) {
      toast.error(formatApiErrorDetail(err) || "No se pudo restaurar");
    } finally {
      setManualResetting(false);
    }
  };

  const updateEventRecipients = (eventName, value) => {
    const recipients = value
      .split(/[\n,]+/)
      .map((s) => s.trim())
      .filter(Boolean);
    setEmailCfg((prev) => ({
      ...prev,
      events: {
        ...prev.events,
        [eventName]: { ...(prev.events?.[eventName] || { enabled: true }), recipients },
      },
    }));
  };

  const toggleEvent = (eventName, enabled) => {
    setEmailCfg((prev) => ({
      ...prev,
      events: {
        ...prev.events,
        [eventName]: { ...(prev.events?.[eventName] || { recipients: [] }), enabled },
      },
    }));
  };

  const catalogPriced = useMemo(
    () => catalog.filter((c) => c.price != null && c.price >= 0),
    [catalog],
  );
  const courseTitle = (id) => catalog.find((c) => c.id === id)?.title || "(eliminado)";

  const savePromo = async () => {
    const p = editing;
    if (!p?.name?.trim()) return toast.error("Pon un nombre a la promoción");
    let body = {
      name: p.name.trim(),
      enabled: !!p.enabled,
      kind: p.kind,
      applies_to: p.applies_to || "both",
      starts_at: p.starts_at || null,
      ends_at: p.ends_at || null,
    };
    if (p.kind === "bulk") {
      body.bulk_percent = Number(p.bulk_percent);
      body.bulk_min_courses = Number(p.bulk_min_courses);
      if (!(body.bulk_percent > 0)) return toast.error("Porcentaje debe ser > 0");
      if (!(body.bulk_min_courses >= 2)) return toast.error("Mínimo 2 cursos");
    } else if (p.kind === "course") {
      body.course_id = p.course_id;
      body.course_discount_type = p.course_discount_type;
      body.course_discount_value = Number(p.course_discount_value);
      if (!body.course_id) return toast.error("Selecciona un curso");
      if (!(body.course_discount_value > 0)) return toast.error("Importe debe ser > 0");
    } else if (p.kind === "package") {
      body.course_ids = p.course_ids || [];
      body.fixed_price = Number(p.fixed_price);
      if (body.course_ids.length < 2) return toast.error("Un paquete necesita al menos 2 cursos");
      if (!(body.fixed_price >= 0)) return toast.error("El precio del paquete no puede ser negativo");
    } else if (p.kind === "quantity") {
      body.quantity_count = Number(p.quantity_count);
      body.fixed_price = Number(p.fixed_price);
      if (!(body.quantity_count >= 2)) return toast.error("La oferta necesita al menos 2 cursos");
      if (!(body.fixed_price > 0)) return toast.error("Pon el precio cerrado de la oferta");
    }
    setSavingPromo(true);
    try {
      if (p.id) await api.put(`/admin/promotions/${p.id}`, body);
      else await api.post("/admin/promotions", body);
      toast.success(p.id ? "Promoción actualizada" : "Promoción creada");
      setEditing(null);
      await loadPromos();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setSavingPromo(false);
    }
  };

  const deletePromo = async (id) => {
    if (!window.confirm("¿Eliminar esta promoción?")) return;
    await api.delete(`/admin/promotions/${id}`);
    toast.success("Promoción eliminada");
    await loadPromos();
  };

  const togglePromoEnabled = async (p) => {
    const body = {
      name: p.name, enabled: !p.enabled, kind: p.kind,
      starts_at: p.starts_at || null, ends_at: p.ends_at || null,
      bulk_percent: p.bulk_percent, bulk_min_courses: p.bulk_min_courses,
      course_id: p.course_id, course_discount_type: p.course_discount_type, course_discount_value: p.course_discount_value,
      course_ids: p.course_ids, fixed_price: p.fixed_price,
    };
    await api.put(`/admin/promotions/${p.id}`, body);
    await loadPromos();
  };

  const savePolicy = async () => {
    if (!policyText.trim()) {
      toast.error("El texto de la política no puede estar vacío");
      return;
    }
    if (
      !window.confirm(
        "Al guardar se incrementará la versión y todos los usuarios deberán aceptar de nuevo. ¿Continuar?",
      )
    )
      return;
    setPolicySaving(true);
    try {
      const { data } = await api.put("/admin/settings/policy", { text: policyText });
      setPolicyVersion(data.version);
      setPolicyUpdatedAt(data.updated_at);
      toast.success(
        `Política guardada (v${data.version}). Los usuarios deberán aceptarla en su próximo inicio de sesión.`,
      );
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setPolicySaving(false);
    }
  };

  const resetPolicyVersion = async () => {
    if (
      !window.confirm(
        "Esto restablece el contador a v1 y BORRA TODOS los consentimientos guardados. Todos los usuarios tendrán que volver a aceptar la política en su próximo inicio de sesión. ¿Continuar?",
      )
    )
      return;
    try {
      const { data } = await api.post("/admin/settings/policy/reset");
      setPolicyVersion(data.version);
      setPolicyUpdatedAt(new Date().toISOString());
      toast.success(
        `Contador restablecido a v${data.version}. Se borraron ${data.consents_cleared} consentimientos.`,
      );
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    }
  };

  const openConsents = async () => {
    setConsentsOpen(true);
    setConsentsLoading(true);
    try {
      const { data } = await api.get("/admin/consents?limit=200");
      setConsents(data);
    } finally {
      setConsentsLoading(false);
    }
  };

  const downloadConsents = async (format) => {
    try {
      const token = localStorage.getItem("token");
      const res = await fetch(`${API}/admin/consents/export.${format}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) throw new Error("No se pudo descargar el archivo");
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `consentimientos.${format}`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    } catch (e) {
      toast.error(e.message);
    }
  };

  return (
    <div data-testid="admin-settings-page">
      <div className="mb-6">
        <h1 className="font-display text-3xl text-brand-ink">Ajustes</h1>
        <p className="text-sm text-gray-500 mt-1">
          Configura los documentos que se entregan al aprobar un test (Certificado normal) o un examen final (Diploma),
          además de la política de privacidad.
        </p>
      </div>

      <Tabs defaultValue="certificate" className="max-w-4xl">
        <TabsList className="mb-4" data-testid="settings-tabs">
          <TabsTrigger value="certificate" data-testid="tab-certificate">
            <Award className="w-4 h-4 mr-2" />
            Certificado
          </TabsTrigger>
          <TabsTrigger value="diploma" data-testid="tab-diploma">
            <GraduationCap className="w-4 h-4 mr-2" />
            Diploma
          </TabsTrigger>
        </TabsList>
        <TabsContent value="certificate">
          <DocumentSettingsPanel kind="certificate" />
        </TabsContent>
        <TabsContent value="diploma">
          <DocumentSettingsPanel kind="diploma" />
        </TabsContent>
      </Tabs>

      <Card
        className="mt-8 p-6 border border-gray-200 shadow-none rounded-lg space-y-4 max-w-3xl"
        data-testid="home-more-info-card"
      >
        <div className="flex items-center justify-between gap-3 flex-wrap">
          <div className="flex items-center gap-2 text-brand-ink">
            <Info className="w-5 h-5" />
            <h2 className="font-display text-lg">Textos editables de la home</h2>
          </div>
          <div className="flex gap-2">
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={() => window.open("/", "_blank", "noopener,noreferrer")}
              data-testid="preview-home-more-info-button"
            >
              <ExternalLink className="w-4 h-4 mr-1" /> Vista previa home
            </Button>
            <Button
              type="button"
              size="sm"
              onClick={saveHomeMoreInfo}
              disabled={homeMoreInfoSaving}
              className="bg-brand hover:bg-brand-hover text-white"
              data-testid="save-home-more-info"
            >
              <Save className="w-4 h-4 mr-1" />
              {homeMoreInfoSaving ? "Guardando…" : "Guardar"}
            </Button>
          </div>
        </div>

        <div className="space-y-2">
          <Label htmlFor="home-highlight" className="text-sm font-medium text-brand-ink">
            Texto destacado del hero
          </Label>
          <p className="text-xs text-gray-500">
            Frase en <strong>negrita</strong> que aparece en la cabecera de la home, debajo del
            subtítulo gris. Pensada para destacar promociones u oportunidades clave (ej. acceso a
            la evaluación de competencias profesionales). Déjalo vacío para ocultarlo.
          </p>
          <Textarea
            id="home-highlight"
            value={homeHighlight}
            onChange={(e) => setHomeHighlight(e.target.value)}
            rows={4}
            placeholder="Con el curso de Atención Sociosanitaria de 300 horas, accederás…"
            data-testid="home-highlight-input"
          />
        </div>

        <div className="space-y-2 pt-2 border-t border-gray-100">
          <Label className="text-sm font-medium text-brand-ink">
            Bloque &quot;+ info&quot; (Markdown)
          </Label>
          <p className="text-xs text-gray-500">
            Texto enriquecido que aparece debajo del hero, dentro de un desplegable
            &quot;+ info&quot;. Si lo dejas vacío, la sección se oculta. Admite negrita, cursiva,
            listas, encabezados y enlaces.
          </p>
          <MarkdownEditor
            value={homeMoreInfo}
            onChange={setHomeMoreInfo}
            rows={10}
            placeholder={"## ¿Quiénes somos?\n\nEn **SIMA Asistencial SL** llevamos más de…\n\n- Punto destacado 1\n- Punto destacado 2"}
            testId="home-more-info-editor"
          />
        </div>
      </Card>

      <Card
        className="mt-8 p-6 border border-gray-200 shadow-none rounded-lg space-y-4 max-w-3xl"
        data-testid="invoice-settings-card"
      >
        <div className="flex items-center justify-between gap-3 flex-wrap">
          <div className="flex items-center gap-2 text-brand-ink">
            <Receipt className="w-5 h-5" />
            <h2 className="font-display text-lg">Datos de facturación</h2>
          </div>
          {canEditInvoiceCfg && (
            <Button
              type="button"
              size="sm"
              onClick={saveInvoiceCfg}
              disabled={invoiceSaving}
              className="bg-brand hover:bg-brand-hover text-white"
              data-testid="save-invoice-cfg"
            >
              <Save className="w-4 h-4 mr-1" />
              {invoiceSaving ? "Guardando…" : "Guardar"}
            </Button>
          )}
        </div>
        <p className="text-xs text-gray-500">
          Estos datos se imprimen como emisor en el encabezado de todos los documentos PDF
          (tickets, facturas y facturas rectificativas). El IVA se aplica sobre la base imponible;
          deja
          <span className="font-mono-data"> 0 </span>
          para mantener la exención de los servicios de enseñanza (Art. 20.1.9 LIVA).
        </p>
        {!canEditInvoiceCfg && (
          <p
            className="text-xs text-amber-900/90 bg-amber-50 border border-amber-200 rounded-md p-3"
            data-testid="invoice-settings-readonly-note"
          >
            Solo lectura: los datos fiscales y el % de IVA únicamente los pueden modificar los
            roles <strong>admin</strong> y <strong>superadmin</strong>.
          </p>
        )}

        <div className="grid md:grid-cols-2 gap-3">
          <div>
            <Label htmlFor="inv-company" className="text-sm font-medium text-brand-ink">
              Empresa <span className="text-red-500">*</span>
            </Label>
            <Input
              id="inv-company"
              value={invoiceCfg.company_name || ""}
              onChange={(e) => setInvoiceCfg((p) => ({ ...p, company_name: e.target.value }))}
              placeholder="SIMA Asistencial SL"
              disabled={!canEditInvoiceCfg}
              data-testid="invoice-company-input"
            />
          </div>
          <div>
            <Label htmlFor="inv-cif" className="text-sm font-medium text-brand-ink">
              CIF / NIF
            </Label>
            <Input
              id="inv-cif"
              value={invoiceCfg.cif || ""}
              onChange={(e) => setInvoiceCfg((p) => ({ ...p, cif: e.target.value }))}
              placeholder="B-12345678"
              disabled={!canEditInvoiceCfg}
              data-testid="invoice-cif-input"
            />
          </div>
          <div className="md:col-span-2">
            <Label htmlFor="inv-address" className="text-sm font-medium text-brand-ink">
              Dirección
            </Label>
            <Textarea
              id="inv-address"
              rows={2}
              value={invoiceCfg.address || ""}
              onChange={(e) => setInvoiceCfg((p) => ({ ...p, address: e.target.value }))}
              placeholder={"Calle Ventura Rodríguez, 18 bajo Derecha\n28008 Madrid (Madrid)"}
              disabled={!canEditInvoiceCfg}
              data-testid="invoice-address-input"
            />
            <p className="text-[11px] text-slate-500 mt-1">
              Pulsa Enter para saltar de línea (aparecerá en la factura tal cual).
            </p>
          </div>
          <div>
            <Label htmlFor="inv-phone" className="text-sm font-medium text-brand-ink">
              Teléfono
            </Label>
            <Input
              id="inv-phone"
              value={invoiceCfg.phone || ""}
              onChange={(e) => setInvoiceCfg((p) => ({ ...p, phone: e.target.value }))}
              placeholder="+34 900 000 000"
              disabled={!canEditInvoiceCfg}
              data-testid="invoice-phone-input"
            />
          </div>
          <div>
            <Label htmlFor="inv-email" className="text-sm font-medium text-brand-ink">
              Email
            </Label>
            <Input
              id="inv-email"
              type="email"
              value={invoiceCfg.email || ""}
              onChange={(e) => setInvoiceCfg((p) => ({ ...p, email: e.target.value }))}
              placeholder="administracion@sima.madrid"
              disabled={!canEditInvoiceCfg}
              data-testid="invoice-email-input"
            />
          </div>
          <div>
            <Label htmlFor="inv-web" className="text-sm font-medium text-brand-ink">
              Web
            </Label>
            <Input
              id="inv-web"
              value={invoiceCfg.web || ""}
              onChange={(e) => setInvoiceCfg((p) => ({ ...p, web: e.target.value }))}
              placeholder="www.sima.madrid"
              disabled={!canEditInvoiceCfg}
              data-testid="invoice-web-input"
            />
          </div>
          <div>
            <Label htmlFor="inv-vat" className="text-sm font-medium text-brand-ink">
              IVA (%)
            </Label>
            <Input
              id="inv-vat"
              type="number"
              step="0.01"
              min="0"
              max="100"
              value={invoiceCfg.vat_percent ?? 0}
              onChange={(e) => setInvoiceCfg((p) => ({ ...p, vat_percent: e.target.value }))}
              placeholder="0"
              disabled={!canEditInvoiceCfg}
              data-testid="invoice-vat-input"
            />
            <p className="text-[11px] text-gray-500 mt-1">
              0 = exento (Art. 20.1.9 LIVA). 21 = IVA general.
            </p>
          </div>
        </div>

        <div className="border-t border-gray-100 pt-4 space-y-2">
          <Label className="text-sm font-medium text-brand-ink flex items-center gap-2">
            <ImageIcon className="w-4 h-4" /> Logo de la factura
          </Label>
          <p className="text-xs text-gray-500">
            PNG o JPG, máx 5 MB. Aparecerá en la esquina superior izquierda del PDF. Si no subes
            uno, se usará el logo por defecto de la marca.
          </p>
          <div className="flex items-center gap-4 flex-wrap">
            <div
              className="w-32 h-20 rounded border border-dashed border-gray-300 bg-gray-50 flex items-center justify-center overflow-hidden"
              data-testid="invoice-logo-preview"
            >
              {invoiceLogoPreview ? (
                <img
                  src={invoiceLogoPreview}
                  alt="Logo factura"
                  className="max-h-full max-w-full object-contain"
                />
              ) : (
                <span className="text-[10px] text-gray-400">Sin logo</span>
              )}
            </div>
            <div className="flex gap-2 flex-wrap">
              <label className="inline-flex">
                <input
                  type="file"
                  accept="image/png,image/jpeg"
                  className="hidden"
                  onChange={(e) => uploadInvoiceLogo(e.target.files?.[0])}
                  data-testid="invoice-logo-input"
                />
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  disabled={invoiceLogoUploading || !canEditInvoiceCfg}
                  onClick={(e) => e.currentTarget.previousElementSibling.click()}
                  data-testid="invoice-logo-upload-button"
                >
                  <Upload className="w-4 h-4 mr-1" />
                  {invoiceLogoUploading
                    ? "Subiendo…"
                    : invoiceLogoPresent
                    ? "Reemplazar logo"
                    : "Subir logo"}
                </Button>
              </label>
              {invoiceLogoPresent && canEditInvoiceCfg && (
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={deleteInvoiceLogo}
                  className="text-red-600 hover:text-red-700"
                  data-testid="invoice-logo-delete-button"
                >
                  <Trash2 className="w-4 h-4 mr-1" /> Eliminar
                </Button>
              )}
            </div>
          </div>
        </div>
      </Card>

      <Card
        className="mt-8 p-6 border border-gray-200 shadow-none rounded-lg space-y-4 max-w-3xl"
        data-testid="email-settings-card"
      >
        <div className="flex items-center justify-between gap-3 flex-wrap">
          <div className="flex items-center gap-2 text-brand-ink">
            <Mail className="w-5 h-5" />
            <h2 className="font-display text-lg">Notificaciones por email (Resend)</h2>
          </div>
          <Button
            type="button"
            size="sm"
            onClick={saveEmailCfg}
            disabled={emailSaving}
            className="bg-brand hover:bg-brand-hover text-white"
            data-testid="save-email-cfg"
          >
            <Save className="w-4 h-4 mr-1" />
            {emailSaving ? "Guardando…" : "Guardar"}
          </Button>
        </div>
        <p className="text-xs text-gray-500">
          Configuración del proveedor de emails transaccionales. Todos los envíos usan Resend con
          el dominio verificado del proyecto. El remitente debe usar el formato
          <span className="font-mono-data"> Nombre &lt;email@dominio&gt;</span> o simplemente
          <span className="font-mono-data"> email@dominio</span>. Si lo dejas vacío, se usará el
          que esté configurado en el backend (variable <span className="font-mono-data">SENDER_EMAIL</span>).
        </p>

        <div className="grid md:grid-cols-2 gap-3">
          <div>
            <Label htmlFor="email-sender" className="text-sm font-medium text-brand-ink">
              Remitente (opcional)
            </Label>
            <Input
              id="email-sender"
              value={emailCfg.sender || ""}
              onChange={(e) => setEmailCfg((p) => ({ ...p, sender: e.target.value }))}
              placeholder="SIMA Asistencial <no-reply@formacion.sima.madrid>"
              data-testid="email-sender-input"
            />
          </div>
          <div>
            <Label htmlFor="email-reply-to" className="text-sm font-medium text-brand-ink">
              Reply-to (uno o más, separados por coma)
            </Label>
            <Input
              id="email-reply-to"
              value={(emailCfg.reply_to || []).join(", ")}
              onChange={(e) =>
                setEmailCfg((p) => ({
                  ...p,
                  reply_to: e.target.value
                    .split(/[\n,]+/)
                    .map((s) => s.trim())
                    .filter(Boolean),
                }))
              }
              placeholder="hablamos@sima.madrid"
              data-testid="email-reply-to-input"
            />
          </div>
        </div>

        <div className="border-t border-gray-100 pt-4 space-y-3">
          <p className="text-xs uppercase tracking-wider text-gray-500 font-mono-data">
            Eventos y destinatarios
          </p>

          <div className="border border-gray-200 rounded-lg p-4 space-y-3" data-testid="event-admin_purchase_notification">
            <div className="flex items-start justify-between gap-3">
              <div>
                <p className="text-sm font-semibold text-brand-ink">
                  Notificación al admin cuando se completa una compra
                </p>
                <p className="text-xs text-gray-500 mt-0.5">
                  Se envía a cada dirección de esta lista tras un pago exitoso, con los datos del
                  comprador, los cursos y la factura PDF adjunta.
                </p>
              </div>
              <Switch
                checked={!!emailCfg.events?.admin_purchase_notification?.enabled}
                onCheckedChange={(v) => toggleEvent("admin_purchase_notification", v)}
                data-testid="toggle-admin_purchase_notification"
              />
            </div>
            <div>
              <Label className="text-xs text-gray-600">Destinatarios (uno por línea o separados por coma)</Label>
              <Textarea
                rows={3}
                value={(emailCfg.events?.admin_purchase_notification?.recipients || []).join("\n")}
                onChange={(e) => updateEventRecipients("admin_purchase_notification", e.target.value)}
                placeholder={"hablamos@sima.madrid\ncarmen.hidalgo@sima.madrid"}
                data-testid="recipients-admin_purchase_notification"
              />
            </div>
          </div>

          <div className="border border-gray-200 rounded-lg p-4 space-y-3" data-testid="event-customer_purchase_confirmation">
            <div className="flex items-start justify-between gap-3">
              <div>
                <p className="text-sm font-semibold text-brand-ink">
                  Confirmación al comprador tras la compra
                </p>
                <p className="text-xs text-gray-500 mt-0.5">
                  Se envía automáticamente <strong>al email introducido en el checkout</strong>. Incluye el resumen del
                  pedido, la factura PDF adjunta y, si es una cuenta recién creada, la contraseña
                  temporal para acceder por primera vez.
                </p>
              </div>
              <Switch
                checked={!!emailCfg.events?.customer_purchase_confirmation?.enabled}
                onCheckedChange={(v) => toggleEvent("customer_purchase_confirmation", v)}
                data-testid="toggle-customer_purchase_confirmation"
              />
            </div>
            <div>
              <Label className="text-xs text-gray-600">
                Destinatarios adicionales (opcional — copia oculta para tu equipo)
              </Label>
              <Textarea
                rows={2}
                value={(emailCfg.events?.customer_purchase_confirmation?.recipients || []).join("\n")}
                onChange={(e) => updateEventRecipients("customer_purchase_confirmation", e.target.value)}
                placeholder={"Déjalo vacío para enviar sólo al comprador"}
                data-testid="recipients-customer_purchase_confirmation"
              />
              <p className="text-[11px] text-gray-400 mt-1">
                Si añades emails aquí, sobrescriben el destino y el correo <strong>NO</strong> irá al comprador.
                Úsalo únicamente para pruebas o auditoría.
              </p>
            </div>
          </div>

          <div className="border border-gray-200 rounded-lg p-4 space-y-3" data-testid="event-account_credentials">
            <div className="flex items-start justify-between gap-3">
              <div>
                <p className="text-sm font-semibold text-brand-ink">
                  Datos de acceso de una cuenta creada desde el panel
                </p>
                <p className="text-xs text-gray-500 mt-0.5">
                  Lleva el email de acceso, la contraseña y el enlace para entrar. Al crear un
                  alumno <strong>se pregunta</strong> si enviarlo; al <strong>regenerar</strong>
                  {" "}una contraseña se envía siempre. Si apagas esto, ninguna de las dos cosas
                  saldrá y tendrás que dar la contraseña a mano.
                </p>
              </div>
              <Switch
                checked={!!emailCfg.events?.account_credentials?.enabled}
                onCheckedChange={(v) => toggleEvent("account_credentials", v)}
                data-testid="toggle-account_credentials"
              />
            </div>
            <div>
              <Label className="text-xs text-gray-600">
                Destinatarios adicionales (opcional — sobrescriben al alumno)
              </Label>
              <Textarea
                rows={2}
                value={(emailCfg.events?.account_credentials?.recipients || []).join("\n")}
                onChange={(e) => updateEventRecipients("account_credentials", e.target.value)}
                placeholder={"Déjalo vacío para enviar sólo al alumno"}
                data-testid="recipients-account_credentials"
              />
              <p className="text-[11px] text-gray-400 mt-1">
                Si añades emails aquí, sobrescriben el destino y el correo <strong>NO</strong> irá al alumno.
                Úsalo únicamente para pruebas o auditoría.
              </p>
            </div>
          </div>

          <div className="border border-gray-200 rounded-lg p-4 space-y-3" data-testid="event-manual_ticket_confirmation">
            <div className="flex items-start justify-between gap-3">
              <div>
                <p className="text-sm font-semibold text-brand-ink">
                  Ticket de un cobro registrado a mano
                </p>
                <p className="text-xs text-gray-500 mt-0.5">
                  Se envía <strong>al alumno</strong> cada vez que registras un pago desde su ficha:
                  el botón «Registrar pago manual» y los de «Generar pago» y Sabadell del selector
                  de cursos. Lleva el ticket en PDF adjunto.
                </p>
              </div>
              <Switch
                checked={!!emailCfg.events?.manual_ticket_confirmation?.enabled}
                onCheckedChange={(v) => toggleEvent("manual_ticket_confirmation", v)}
                data-testid="toggle-manual_ticket_confirmation"
              />
            </div>
            <div>
              <Label className="text-xs text-gray-600">
                Destinatarios adicionales (opcional — sobrescriben al alumno)
              </Label>
              <Textarea
                rows={2}
                value={(emailCfg.events?.manual_ticket_confirmation?.recipients || []).join("\n")}
                onChange={(e) => updateEventRecipients("manual_ticket_confirmation", e.target.value)}
                placeholder={"Déjalo vacío para enviar sólo al alumno"}
                data-testid="recipients-manual_ticket_confirmation"
              />
              <p className="text-[11px] text-gray-400 mt-1">
                Si añades emails aquí, sobrescriben el destino y el correo <strong>NO</strong> irá al alumno.
                Úsalo únicamente para pruebas o auditoría.
              </p>
            </div>
          </div>

          <div className="border border-gray-200 rounded-lg p-4 space-y-3" data-testid="event-installment_paid_confirmation">
            <div className="flex items-start justify-between gap-3">
              <div>
                <p className="text-sm font-semibold text-brand-ink">
                  Confirmación al marcar un plazo de pago aplazado como pagado
                </p>
                <p className="text-xs text-gray-500 mt-0.5">
                  Se envía automáticamente <strong>al alumno del plan</strong> cada vez que un gestor marca un plazo
                  como pagado. Incluye el ticket de ese plazo en PDF adjunto.
                </p>
              </div>
              <Switch
                checked={!!emailCfg.events?.installment_paid_confirmation?.enabled}
                onCheckedChange={(v) => toggleEvent("installment_paid_confirmation", v)}
                data-testid="toggle-installment_paid_confirmation"
              />
            </div>
            <div>
              <Label className="text-xs text-gray-600">
                Destinatarios adicionales (opcional — sobrescriben al alumno)
              </Label>
              <Textarea
                rows={2}
                value={(emailCfg.events?.installment_paid_confirmation?.recipients || []).join("\n")}
                onChange={(e) => updateEventRecipients("installment_paid_confirmation", e.target.value)}
                placeholder={"Déjalo vacío para enviar sólo al alumno"}
                data-testid="recipients-installment_paid_confirmation"
              />
              <p className="text-[11px] text-gray-400 mt-1">
                Si añades emails aquí, sobrescriben el destino y el correo <strong>NO</strong> irá al alumno.
                Úsalo únicamente para pruebas o auditoría.
              </p>
            </div>
          </div>

          <div className="border border-gray-200 rounded-lg p-4 space-y-3" data-testid="event-diploma_available_notification">
            <div className="flex items-start justify-between gap-3">
              <div>
                <p className="text-sm font-semibold text-brand-ink">
                  Aviso de diploma o certificado disponible
                </p>
                <p className="text-xs text-gray-500 mt-0.5">
                  Se envía <strong>al alumno</strong> al cobrar el <strong>último plazo</strong> de su pago aplazado,
                  siempre que ya tenga el curso aprobado: es el momento en que se le desbloquea la descarga.
                  Si aún le faltan exámenes, no se envía nada.
                </p>
              </div>
              <Switch
                checked={!!emailCfg.events?.diploma_available_notification?.enabled}
                onCheckedChange={(v) => toggleEvent("diploma_available_notification", v)}
                data-testid="toggle-diploma_available_notification"
              />
            </div>
            <div>
              <Label className="text-xs text-gray-600">
                Destinatarios adicionales (opcional — sobrescriben al alumno)
              </Label>
              <Textarea
                rows={2}
                value={(emailCfg.events?.diploma_available_notification?.recipients || []).join("\n")}
                onChange={(e) => updateEventRecipients("diploma_available_notification", e.target.value)}
                placeholder={"Déjalo vacío para enviar sólo al alumno"}
                data-testid="recipients-diploma_available_notification"
              />
              <p className="text-[11px] text-gray-400 mt-1">
                Si añades emails aquí, sobrescriben el destino y el correo <strong>NO</strong> irá al alumno.
                Úsalo únicamente para pruebas o auditoría.
              </p>
            </div>
          </div>

          <div className="border border-gray-200 rounded-lg p-4 space-y-3" data-testid="event-customer_inactivity_reminder">
            <div className="flex items-start justify-between gap-3">
              <div>
                <p className="text-sm font-semibold text-brand-ink">
                  Recordatorio de inactividad
                </p>
                <p className="text-xs text-gray-500 mt-0.5">
                  Correo pensado para <strong>usuarios inactivos con cursos pendientes</strong> (por defecto {'>'} 7 días
                  sin acceso). Se dispara manualmente desde el panel <em>Recordatorios de inactividad</em> más abajo.
                  Puedes previsualizar la lista antes de enviar.
                </p>
              </div>
              <Switch
                checked={!!emailCfg.events?.customer_inactivity_reminder?.enabled}
                onCheckedChange={(v) => toggleEvent("customer_inactivity_reminder", v)}
                data-testid="toggle-customer_inactivity_reminder"
              />
            </div>
            <div>
              <Label className="text-xs text-gray-600">
                Destinatarios adicionales (opcional — sobrescriben al alumno)
              </Label>
              <Textarea
                rows={2}
                value={(emailCfg.events?.customer_inactivity_reminder?.recipients || []).join("\n")}
                onChange={(e) => updateEventRecipients("customer_inactivity_reminder", e.target.value)}
                placeholder={"Déjalo vacío para enviar a cada alumno inactivo"}
                data-testid="recipients-customer_inactivity_reminder"
              />
              <p className="text-[11px] text-gray-400 mt-1">
                Igual que en la confirmación al comprador: si añades emails aquí, el recordatorio irá a esa
                dirección en lugar de al alumno. Úsalo únicamente para pruebas.
              </p>
            </div>
          </div>

          <details className="text-xs text-gray-500 cursor-pointer">
            <summary className="font-mono-data uppercase tracking-wider">Eventos próximamente disponibles</summary>
            <ul className="mt-2 space-y-1 pl-4 list-disc">
              <li>Bienvenida al usuario creado manualmente por el admin</li>
              <li>Curso aprobado (con certificado/diploma adjunto)</li>
            </ul>
          </details>
        </div>

        <div className="border-t border-gray-100 pt-4 flex items-end gap-2 flex-wrap">
          <div className="flex-1 min-w-[200px]">
            <Label htmlFor="email-test-to" className="text-sm font-medium text-brand-ink">
              Enviar email de prueba
            </Label>
            <Input
              id="email-test-to"
              type="email"
              value={emailTestTo}
              onChange={(e) => setEmailTestTo(e.target.value)}
              placeholder="tu@correo.com"
              data-testid="email-test-to"
            />
          </div>
          <Button
            type="button"
            variant="outline"
            onClick={sendTestEmail}
            disabled={emailTesting}
            data-testid="send-test-email-btn"
          >
            <Send className="w-4 h-4 mr-1" />
            {emailTesting ? "Enviando…" : "Enviar prueba"}
          </Button>
        </div>
      </Card>

      <Card
        className="mt-8 p-6 border border-gray-200 shadow-none rounded-lg space-y-4 max-w-5xl"
        data-testid="inactivity-reminders-card"
      >
        <div className="flex items-center gap-2 text-brand-ink">
          <Clock className="w-5 h-5" />
          <h2 className="font-display text-lg">Recordatorios de inactividad</h2>
        </div>
        <p className="text-sm text-gray-500">
          Envía un correo motivador a los alumnos que llevan más de N días sin acceder <strong>y aún tienen
          cursos pendientes</strong>. Cuando el interruptor del evento &quot;Recordatorio de inactividad&quot; está apagado
          arriba, la lista se puede consultar pero no permitirá enviar.
        </p>

        <div className="flex items-end gap-3 flex-wrap">
          <div>
            <Label htmlFor="inactivity-days" className="text-sm">Días sin acceder ≥</Label>
            <Input
              id="inactivity-days"
              type="number"
              min={1}
              max={365}
              value={inactivityDays}
              onChange={(e) => setInactivityDays(Number(e.target.value) || 7)}
              className="w-28"
              data-testid="inactivity-days-input"
            />
          </div>
          <Button
            type="button"
            variant="outline"
            onClick={loadInactiveUsers}
            disabled={inactiveLoading}
            data-testid="inactivity-refresh-btn"
          >
            <UsersIcon className="w-4 h-4 mr-1" />
            {inactiveLoading ? "Cargando…" : "Actualizar lista"}
          </Button>
          <Button
            type="button"
            onClick={sendInactivityReminders}
            disabled={inactiveSending || inactiveUsers.length === 0 || !emailCfg.events?.customer_inactivity_reminder?.enabled}
            className="bg-brand hover:bg-brand-hover text-white"
            data-testid="inactivity-send-btn"
            title={!emailCfg.events?.customer_inactivity_reminder?.enabled ? "Activa el evento en el bloque de emails" : ""}
          >
            <Send className="w-4 h-4 mr-1" />
            {inactiveSending
              ? "Enviando…"
              : inactiveSelected.length > 0
                ? `Enviar a los ${inactiveSelected.length} seleccionados`
                : `Enviar a todos (${inactiveUsers.length})`}
          </Button>
        </div>

        {inactiveUsers.length > 0 && (
          <div className="border border-gray-200 rounded-lg overflow-hidden" data-testid="inactivity-users-table">
            <table className="w-full text-sm">
              <thead className="bg-gray-50 border-b border-gray-200 text-xs uppercase tracking-wider text-gray-500">
                <tr>
                  <th className="text-left px-3 py-2 w-8"></th>
                  <th className="text-left px-3 py-2">Alumno</th>
                  <th className="text-left px-3 py-2">Email</th>
                  <th className="text-right px-3 py-2">Días</th>
                  <th className="text-right px-3 py-2">Pendientes</th>
                </tr>
              </thead>
              <tbody>
                {inactiveUsers.map((u) => (
                  <tr key={u.id} className="border-b border-gray-100 last:border-b-0" data-testid={`inactivity-row-${u.email}`}>
                    <td className="px-3 py-2">
                      <Checkbox
                        checked={inactiveSelected.includes(u.id)}
                        onCheckedChange={() => toggleInactiveSelected(u.id)}
                        data-testid={`inactivity-check-${u.email}`}
                      />
                    </td>
                    <td className="px-3 py-2 text-brand-ink">
                      {(u.first_name || u.last_name) ? `${u.first_name} ${u.last_name}`.trim() : "—"}
                    </td>
                    <td className="px-3 py-2 text-gray-600 truncate max-w-[220px]">{u.email}</td>
                    <td className="px-3 py-2 text-right font-mono-data text-gray-600">
                      {u.days_inactive === null || u.days_inactive === undefined ? "nunca" : u.days_inactive}
                    </td>
                    <td className="px-3 py-2 text-right font-mono-data text-brand-ink">{u.pending_courses.length}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {!inactiveLoading && inactiveUsers.length === 0 && (
          <p className="text-sm text-gray-400 italic">
            Pulsa &quot;Actualizar lista&quot; para ver los alumnos que cumplen el criterio.
          </p>
        )}
      </Card>

      {isSuperadmin && (
        <Card
          className="mt-8 p-6 border border-amber-200 bg-amber-50/30 shadow-none rounded-lg space-y-4 max-w-5xl"
          data-testid="test-data-card"
        >
          <div className="flex items-center gap-2 text-amber-900">
            <FlaskConical className="w-5 h-5" />
            <h2 className="font-display text-lg">Datos de pruebas</h2>
          </div>
          <p className="text-xs text-gray-600">
            Todo lo que genera la cuenta de pruebas —recibos con numeración{" "}
            <span className="font-mono-data">0000</span>, cursos asignados, exámenes y planes de
            pago— se puede borrar de golpe cuando ya no haga falta. Los contadores de numeración
            no se tocan: un documento de prueba nunca consumió ningún número.
          </p>

          {!testData ? (
            <p className="text-sm text-gray-400 italic">Cargando…</p>
          ) : testData.accounts.length === 0 ? (
            <p className="text-sm text-gray-600 bg-white border border-gray-200 rounded-md p-3">
              No hay ninguna cuenta marcada como de pruebas. Márcala en{" "}
              <strong>Personas usuarias</strong> con el botón del matraz.
            </p>
          ) : (
            <>
              <p className="text-sm text-brand-ink">
                Cuenta{testData.accounts.length === 1 ? "" : "s"} de pruebas:{" "}
                {testData.accounts.map((a) => (
                  <span key={a.id} className="font-medium">
                    {a.name || a.email}{" "}
                    <span className="text-gray-500 font-mono-data text-xs">({a.email})</span>
                  </span>
                ))}
              </p>
              <div className="flex flex-wrap gap-2" data-testid="test-data-counts">
                {[
                  ["documents", "documentos"],
                  ["assignments", "asignaciones"],
                  ["attempts", "exámenes"],
                  ["installment_plans", "planes de pago"],
                  ["discount_code_uses", "códigos canjeados"],
                ].map(([key, label]) => (
                  <span
                    key={key}
                    className={`text-xs px-2 py-1 rounded border font-mono-data ${
                      testData.counts[key] > 0
                        ? "bg-white border-amber-300 text-amber-900"
                        : "bg-gray-50 border-gray-200 text-gray-400"
                    }`}
                  >
                    {testData.counts[key]} {label}
                  </span>
                ))}
              </div>
              <div className="flex items-center gap-3">
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={() => setTestConfirm(true)}
                  disabled={testData.total === 0}
                  className="border-red-300 text-red-700 hover:bg-red-50"
                  data-testid="test-data-purge"
                >
                  <Trash2 className="w-4 h-4 mr-1" />
                  Borrar datos de pruebas
                </Button>
                <Button type="button" variant="ghost" size="sm" onClick={reloadTestData}>
                  <RotateCcw className="w-4 h-4 mr-1" /> Actualizar
                </Button>
                {testData.total === 0 && (
                  <span className="text-xs text-gray-500">No hay nada que borrar.</span>
                )}
              </div>
            </>
          )}

          <Dialog open={testConfirm} onOpenChange={(o) => !o && setTestConfirm(false)}>
            <DialogContent data-testid="test-data-dialog">
              <DialogHeader>
                <DialogTitle>¿Borrar todos los datos de prueba?</DialogTitle>
                <DialogDescription asChild>
                  <div className="space-y-3 text-sm">
                    <p>
                      Se eliminarán <strong>{testData?.total}</strong> registros generados por la
                      cuenta de pruebas: sus recibos, cursos asignados, exámenes realizados, planes
                      de pago y códigos canjeados.
                    </p>
                    <p className="text-amber-900 bg-amber-50 border border-amber-200 rounded-md p-3">
                      <strong>No hay vuelta atrás</strong> y no se guarda copia. La cuenta se
                      conserva —con su contraseña y su marca de pruebas— lista para volver a usarse.
                      Nada de ningún alumno real se toca.
                    </p>
                  </div>
                </DialogDescription>
              </DialogHeader>
              <DialogFooter>
                <Button variant="ghost" onClick={() => setTestConfirm(false)}>Cancelar</Button>
                <Button
                  onClick={purgeTestData}
                  disabled={testPurging}
                  className="bg-red-600 hover:bg-red-700 text-white"
                  data-testid="test-data-confirm"
                >
                  {testPurging
                    ? <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    : <Trash2 className="w-4 h-4 mr-2" />}
                  Sí, borrar
                </Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
        </Card>
      )}

      <Card
        className="mt-8 p-6 border border-gray-200 shadow-none rounded-lg space-y-4 max-w-5xl"
        data-testid="manuals-settings-card"
      >
        <div className="flex items-center justify-between gap-3 flex-wrap">
          <div className="flex items-center gap-2 text-brand-ink">
            <BookOpen className="w-5 h-5" />
            <h2 className="font-display text-lg">Manuales de uso (editables)</h2>
          </div>
          <div className="flex items-center gap-2">
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={() => setManualPreview((p) => !p)}
              data-testid="manual-toggle-preview"
            >
              {manualPreview ? <><EyeOff className="w-4 h-4 mr-1" /> Editar</> : <><Eye className="w-4 h-4 mr-1" /> Vista previa</>}
            </Button>
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={resetManual}
              disabled={manualResetting || !manuals[manualVariant].custom}
              className="text-red-600 hover:text-red-700"
              data-testid="manual-reset"
              title={manuals[manualVariant].custom ? "Restaurar al texto original del sistema" : "Ya usas el texto por defecto"}
            >
              <RotateCcw className="w-4 h-4 mr-1" />
              {manualResetting ? "…" : "Restaurar"}
            </Button>
            <Button
              type="button"
              size="sm"
              onClick={saveManual}
              disabled={manualSaving}
              className="bg-brand hover:bg-brand-hover text-white"
              data-testid="manual-save"
            >
              <Save className="w-4 h-4 mr-1" />
              {manualSaving ? "Guardando…" : "Guardar"}
            </Button>
          </div>
        </div>
        <p className="text-xs text-gray-500">
          Los manuales se sirven al panel de ayuda desde aquí. Si dejas la versión por defecto, se
          usa el Markdown del sistema (versionado con el código). Al guardar una versión propia, se
          persiste en base de datos y prevalece hasta que pulses «Restaurar».
        </p>

        <div className="inline-flex rounded-md border border-gray-200 bg-gray-50 p-0.5" role="tablist">
          {[
            ...(isGestion ? [] : [{ key: "admin", label: "Manual del administrador" }]),
            { key: "gestion", label: "Manual del rol gestión" },
            { key: "user",    label: "Manual del usuario"     },
          ].map((t) => {
            const active = manualVariant === t.key;
            return (
              <button
                key={t.key}
                type="button"
                onClick={() => setManualVariant(t.key)}
                className={`px-3 py-1.5 text-xs font-medium rounded flex items-center gap-1.5 transition-colors ${
                  active ? "bg-white shadow-sm text-brand-ink" : "text-gray-500 hover:text-gray-700"
                }`}
                data-testid={`manual-tab-${t.key}`}
              >
                {t.label}
                <span
                  className={`ml-1 text-[9px] font-mono-data px-1 py-0.5 rounded border ${
                    manuals[t.key].custom
                      ? "bg-amber-50 text-amber-800 border-amber-200"
                      : "bg-gray-100 text-gray-600 border-gray-200"
                  }`}
                >
                  {manuals[t.key].custom ? "personalizado" : "por defecto"}
                </span>
              </button>
            );
          })}
        </div>

        {manualPreview ? (
          <div
            className="border border-gray-200 rounded p-4 bg-white max-h-[70vh] overflow-y-auto"
            data-testid="manual-preview"
          >
            <MarkdownView text={manualEdits[manualVariant] || ""} />
          </div>
        ) : (
          <Textarea
            value={manualEdits[manualVariant] || ""}
            onChange={(e) =>
              setManualEdits((prev) => ({ ...prev, [manualVariant]: e.target.value }))
            }
            rows={22}
            className="font-mono-data text-xs leading-relaxed"
            placeholder="# Escribe aquí el manual en Markdown…"
            data-testid={`manual-editor-${manualVariant}`}
          />
        )}
      </Card>

      <Card
        className="mt-8 p-6 border border-gray-200 shadow-none rounded-lg space-y-4 max-w-3xl"
        data-testid="policy-card"
      >
        <div className="flex items-center justify-between gap-3 flex-wrap">
          <div className="flex items-center gap-2 text-brand-ink">
            <Shield className="w-5 h-5" />
            <h2 className="font-display text-lg">Política de privacidad</h2>
            {policyVersion ? (
              <span className="text-xs font-mono-data text-gray-500 ml-1">
                v{policyVersion}
                {policyUpdatedAt
                  ? ` · actualizada ${new Date(policyUpdatedAt).toLocaleDateString("es-ES")}`
                  : ""}
              </span>
            ) : null}
          </div>
          <div className="flex gap-2 flex-wrap">
            <Button type="button" variant="outline" onClick={openConsents} data-testid="open-consents">
              <History className="w-4 h-4 mr-1" />
              Auditoría de consentimientos
            </Button>
            <Button
              type="button"
              variant="outline"
              onClick={resetPolicyVersion}
              className="border-amber-300 text-amber-700 hover:bg-amber-50"
              data-testid="reset-policy-version"
            >
              <RotateCcw className="w-4 h-4 mr-1" />
              Restablecer contador a v1
            </Button>
            <Button
              type="button"
              onClick={savePolicy}
              disabled={policySaving}
              className="bg-brand hover:bg-brand-hover text-white"
              data-testid="save-policy"
            >
              <Save className="w-4 h-4 mr-1" />
              {policySaving ? "Guardando…" : "Guardar política"}
            </Button>
          </div>
        </div>
        <p className="text-xs text-gray-500">
          Al guardar se incrementa la versión y todos los usuarios deberán volver a aceptar
          la política en su próximo inicio de sesión.
        </p>
        <Textarea
          value={policyText}
          onChange={(e) => setPolicyText(e.target.value)}
          rows={12}
          className="font-mono-data text-xs leading-relaxed"
          placeholder="Escribe aquí el texto completo de la política de privacidad y protección de datos…"
          data-testid="policy-text-input"
        />
      </Card>

      {canPromotions && (
      <Card
        className="mt-8 p-6 border border-gray-200 shadow-none rounded-lg space-y-4 max-w-5xl"
        data-testid="promotions-card"
      >
        <div className="flex items-center justify-between gap-3 flex-wrap">
          <div className="flex items-center gap-2 text-brand-ink">
            <Tag className="w-5 h-5" />
            <h2 className="font-display text-lg">Promociones (catálogo público)</h2>
            <span className="text-xs text-gray-500 ml-1">{promotions.length} activas/inactivas</span>
          </div>
          <div className="flex gap-2">
            <Button
              type="button"
              variant="outline"
              onClick={() => window.open("/", "_blank", "noopener,noreferrer")}
              data-testid="preview-home-promotions-button"
              title="Abre la home pública en una pestaña nueva para ver las promociones aplicadas"
            >
              <ExternalLink className="w-4 h-4 mr-1" />
              Vista previa home
            </Button>
            <Button
              type="button"
              onClick={() => setEditing({ ...EMPTY_PROMO })}
              className="bg-brand hover:bg-brand-hover text-white"
              data-testid="add-promotion-btn"
            >
              <Plus className="w-4 h-4 mr-1" />
              Nueva promoción
            </Button>
          </div>
        </div>
        <p className="text-xs text-gray-500">
          Combina varias reglas para crear promociones permanentes y ofertas especiales.
          Tres tipos: <strong>descuento por lote</strong> (% al alcanzar N cursos),{" "}
          <strong>descuento por curso</strong> (% o €) y{" "}
          <strong>paquete</strong> (precio fijo para un set de cursos).
          Se aplican en este orden: paquetes → descuentos por curso → descuento por lote.
        </p>

        {promotions.length === 0 ? (
          <p className="text-sm text-gray-500 italic" data-testid="no-promotions">
            Aún no hay promociones. Crea la primera con el botón &laquo;Nueva promoción&raquo;.
          </p>
        ) : (
          <ul className="divide-y divide-gray-100 border border-gray-100 rounded-md">
            {promotions.map((p) => (
              <li
                key={p.id}
                className="flex items-center justify-between gap-3 px-3 py-3 flex-wrap"
                data-testid={`promo-row-${p.id}`}
              >
                <div className="flex-1 min-w-[260px]">
                  <div className="flex items-center gap-2 flex-wrap">
                    {p.kind === "package" ? (
                      <Package className="w-4 h-4 text-[#8a6f43]" />
                    ) : p.kind === "course" ? (
                      <Tag className="w-4 h-4 text-rose-600" />
                    ) : (
                      <Tag className="w-4 h-4 text-brand" />
                    )}
                    <span className="font-medium text-gray-900">{p.name}</span>
                    <span className={`text-[10px] tracking-[0.15em] uppercase px-2 py-0.5 rounded-full font-mono-data ${
                      p.kind === "package" ? "bg-[#fdf2d9] text-[#8a6f43] border border-[#e7d5b4]"
                      : p.kind === "quantity" ? "bg-violet-50 text-violet-700 border border-violet-200"
                      : p.kind === "course" ? "bg-rose-50 text-rose-700 border border-rose-200"
                      : "bg-brand/10 text-brand border border-brand/20"
                    }`}>
                      {p.kind === "package" ? "Paquete"
                        : p.kind === "quantity" ? `${p.quantity_count}x`
                        : p.kind === "course" ? "Curso" : "Lote"}
                    </span>
                    {(p.applies_to || "both") !== "both" && (
                      <span
                        className="text-[10px] px-2 py-0.5 rounded-full bg-gray-100 text-gray-600 border border-gray-200"
                        data-testid={`promo-scope-${p.id}`}
                      >
                        {APPLIES_TO_LABELS[p.applies_to]}
                      </span>
                    )}
                  </div>
                  <p className="text-xs text-gray-500 mt-1">
                    {p.kind === "bulk" && `${p.bulk_percent}% a partir de ${p.bulk_min_courses} cursos`}
                    {p.kind === "quantity" && `${p.quantity_count} cursos por ${p.fixed_price} €`}
                    {p.kind === "course" && (
                      <>
                        {p.course_discount_type === "percent"
                          ? `−${p.course_discount_value}%`
                          : `−${p.course_discount_value} €`}{" "}
                        sobre <span className="font-medium text-gray-700">{courseTitle(p.course_id)}</span>
                      </>
                    )}
                    {p.kind === "package" && (
                      <>
                        {p.fixed_price} € · {(p.course_ids || []).length} cursos:{" "}
                        <span className="font-medium text-gray-700">
                          {(p.course_ids || []).map(courseTitle).join(" + ")}
                        </span>
                      </>
                    )}
                  </p>
                  {(p.starts_at || p.ends_at) && (
                    <p
                      className="text-[10px] text-amber-700 font-mono-data mt-1"
                      data-testid={`promo-dates-${p.id}`}
                    >
                      📅 {p.starts_at ? `desde ${p.starts_at}` : "sin inicio"}
                      {" — "}
                      {p.ends_at ? `hasta ${p.ends_at}` : "sin fin"}
                    </p>
                  )}
                </div>
                <div className="flex items-center gap-2">
                  <Switch
                    checked={!!p.enabled}
                    onCheckedChange={() => togglePromoEnabled(p)}
                    data-testid={`promo-toggle-${p.id}`}
                  />
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => setEditing({ ...EMPTY_PROMO, ...p, course_ids: p.course_ids || [] })}
                    data-testid={`promo-edit-${p.id}`}
                  >
                    <Edit className="w-3.5 h-3.5" />
                  </Button>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => deletePromo(p.id)}
                    data-testid={`promo-delete-${p.id}`}
                  >
                    <Trash2 className="w-3.5 h-3.5 text-red-500" />
                  </Button>
                </div>
              </li>
            ))}
          </ul>
        )}
      </Card>
      )}

      {/* Promotion editor modal */}
      <Dialog open={!!editing} onOpenChange={(o) => !o && setEditing(null)}>
        <DialogContent className="max-w-xl max-h-[90vh] overflow-y-auto" data-testid="promo-modal">
          {editing && (
            <>
              <DialogHeader>
                <DialogTitle>
                  {editing.id ? "Editar promoción" : "Nueva promoción"}
                </DialogTitle>
              </DialogHeader>
              <div className="space-y-4">
                <div>
                  <Label htmlFor="pname">Nombre</Label>
                  <Input
                    id="pname"
                    value={editing.name}
                    onChange={(e) => setEditing({ ...editing, name: e.target.value })}
                    placeholder='Ej: "Black Friday 2026", "Pack inicial", "Oferta curso de RCP"'
                    data-testid="promo-name-input"
                  />
                </div>
                <div className="flex items-center justify-between gap-3">
                  <div className="flex-1">
                    <Label htmlFor="pkind">Tipo</Label>
                    <Select
                      value={editing.kind}
                      onValueChange={(v) => setEditing({ ...editing, kind: v })}
                    >
                      <SelectTrigger id="pkind" data-testid="promo-kind-select">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="bulk">Descuento por lote (% en N+ cursos)</SelectItem>
                        <SelectItem value="course">Descuento sobre un curso</SelectItem>
                        <SelectItem value="package">Paquete a precio fijo</SelectItem>
                        <SelectItem value="quantity">Oferta 2x, 3x… a precio cerrado</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="flex items-center gap-2 pt-6">
                    <Label htmlFor="pena" className="text-sm">Activa</Label>
                    <Switch
                      id="pena"
                      checked={!!editing.enabled}
                      onCheckedChange={(v) => setEditing({ ...editing, enabled: v })}
                      data-testid="promo-enabled-switch"
                    />
                  </div>
                </div>

                <div>
                  <Label htmlFor="pscope">¿A qué se aplica?</Label>
                  <Select
                    value={editing.applies_to || "both"}
                    onValueChange={(v) => setEditing({ ...editing, applies_to: v })}
                  >
                    <SelectTrigger id="pscope" data-testid="promo-applies-to-select">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="both">Cursos y talleres</SelectItem>
                      <SelectItem value="courses">Solo cursos</SelectItem>
                      <SelectItem value="workshops">Solo talleres</SelectItem>
                    </SelectContent>
                  </Select>
                  <p className="text-[11px] text-gray-500 mt-1">
                    La promoción solo mira lo que entra en este ámbito: ni lo descuenta ni cuenta
                    para alcanzar el mínimo.
                  </p>
                </div>

                <div className="grid grid-cols-2 gap-3" data-testid="promo-date-range">
                  <div>
                    <Label htmlFor="pstart">Fecha inicio (opcional)</Label>
                    <Input
                      id="pstart"
                      type="date"
                      value={editing.starts_at || ""}
                      onChange={(e) => setEditing({ ...editing, starts_at: e.target.value })}
                      data-testid="promo-starts-at"
                    />
                  </div>
                  <div>
                    <Label htmlFor="pend">Fecha fin (opcional)</Label>
                    <Input
                      id="pend"
                      type="date"
                      value={editing.ends_at || ""}
                      onChange={(e) => setEditing({ ...editing, ends_at: e.target.value })}
                      data-testid="promo-ends-at"
                    />
                  </div>
                  <p className="col-span-2 text-[11px] text-gray-500 -mt-2">
                    Si dejas las dos vacías, la promoción es permanente. El backend la oculta automáticamente fuera del rango.
                  </p>
                </div>

                {editing.kind === "bulk" && (
                  <div className="grid grid-cols-2 gap-3" data-testid="promo-fields-bulk">
                    <div>
                      <Label>Descuento (%)</Label>
                      <Input
                        type="number" min={0} max={100} step="0.5"
                        value={editing.bulk_percent}
                        onChange={(e) => setEditing({ ...editing, bulk_percent: e.target.value })}
                        data-testid="promo-bulk-percent"
                      />
                    </div>
                    <div>
                      <Label>Cursos mínimos</Label>
                      <Input
                        type="number" min={2}
                        value={editing.bulk_min_courses}
                        onChange={(e) => setEditing({ ...editing, bulk_min_courses: e.target.value })}
                        data-testid="promo-bulk-min"
                      />
                    </div>
                  </div>
                )}

                {editing.kind === "course" && (
                  <div className="space-y-3" data-testid="promo-fields-course">
                    <div>
                      <Label>Curso</Label>
                      <Select
                        value={editing.course_id}
                        onValueChange={(v) => setEditing({ ...editing, course_id: v })}
                      >
                        <SelectTrigger data-testid="promo-course-select">
                          <SelectValue placeholder="Selecciona un curso…" />
                        </SelectTrigger>
                        <SelectContent>
                          {catalogPriced.map((c) => (
                            <SelectItem key={c.id} value={c.id}>
                              {c.title} · {c.price} €
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                      {catalogPriced.length === 0 && (
                        <p className="text-[11px] text-amber-700 mt-1">
                          No hay cursos con precio configurado. Edita un curso y ponle precio primero.
                        </p>
                      )}
                    </div>
                    <div className="grid grid-cols-2 gap-3">
                      <div>
                        <Label>Tipo</Label>
                        <Select
                          value={editing.course_discount_type}
                          onValueChange={(v) => setEditing({ ...editing, course_discount_type: v })}
                        >
                          <SelectTrigger data-testid="promo-course-type">
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="percent">Porcentaje (%)</SelectItem>
                            <SelectItem value="fixed">Importe fijo (€)</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                      <div>
                        <Label>Valor</Label>
                        <Input
                          type="number" min={0} step="0.5"
                          value={editing.course_discount_value}
                          onChange={(e) => setEditing({ ...editing, course_discount_value: e.target.value })}
                          data-testid="promo-course-value"
                        />
                      </div>
                    </div>
                  </div>
                )}

                {editing.kind === "quantity" && (
                  <div className="grid grid-cols-2 gap-3" data-testid="promo-fields-quantity">
                    <div>
                      <Label>Nº de cursos de la oferta</Label>
                      <Input
                        type="number" min={2} step="1"
                        value={editing.quantity_count}
                        onChange={(e) => setEditing({ ...editing, quantity_count: e.target.value })}
                        data-testid="promo-quantity-count"
                      />
                    </div>
                    <div>
                      <Label>Precio cerrado (€)</Label>
                      <Input
                        type="number" min={0} step="0.5"
                        value={editing.fixed_price}
                        onChange={(e) => setEditing({ ...editing, fixed_price: e.target.value })}
                        data-testid="promo-quantity-price"
                      />
                    </div>
                    <p className="col-span-2 text-[11px] text-gray-500 -mt-1">
                      <strong>{editing.quantity_count || 2}x{editing.fixed_price || 0} €</strong>:
                      el cliente elige {editing.quantity_count || 2} cursos cualesquiera del ámbito
                      y paga ese precio. Se repite mientras queden suficientes en el carrito (con
                      una oferta 2x y 4 cursos se aplica dos veces) y se agrupan primero los más
                      caros. Si el grupo costara menos que el precio cerrado, no se aplica.
                    </p>
                  </div>
                )}

                {editing.kind === "package" && (
                  <div className="space-y-3" data-testid="promo-fields-package">
                    <div>
                      <Label>Precio fijo del paquete (€)</Label>
                      <Input
                        type="number" min={0} step="0.5"
                        value={editing.fixed_price}
                        onChange={(e) => setEditing({ ...editing, fixed_price: e.target.value })}
                        data-testid="promo-package-price"
                      />
                    </div>
                    <div>
                      <Label>Cursos del paquete</Label>
                      <p className="text-[11px] text-gray-500 mb-2">
                        Marca los cursos que componen el paquete (mínimo 2). Solo se aplica si el cliente los selecciona TODOS.
                      </p>
                      {catalogPriced.length === 0 ? (
                        <p className="text-[11px] text-amber-700">No hay cursos con precio.</p>
                      ) : (
                        <ul className="border border-gray-200 rounded-md max-h-60 overflow-y-auto divide-y divide-gray-100">
                          {catalogPriced.map((c) => {
                            const checked = (editing.course_ids || []).includes(c.id);
                            return (
                              <li key={c.id} className="flex items-center justify-between px-3 py-2 text-sm">
                                <label className="flex items-center gap-2 cursor-pointer flex-1">
                                  <Checkbox
                                    checked={checked}
                                    onCheckedChange={() => {
                                      const ids = new Set(editing.course_ids || []);
                                      checked ? ids.delete(c.id) : ids.add(c.id);
                                      setEditing({ ...editing, course_ids: Array.from(ids) });
                                    }}
                                    data-testid={`promo-pkg-course-${c.id}`}
                                  />
                                  <span className="text-gray-800">{c.title}</span>
                                </label>
                                <span className="text-gray-500 font-mono-data text-xs">{c.price} €</span>
                              </li>
                            );
                          })}
                        </ul>
                      )}
                      {(editing.course_ids || []).length >= 2 && (
                        <p className="text-xs text-brand-ink mt-2">
                          Suma normal:{" "}
                          <span className="font-mono-data">
                            {(editing.course_ids || []).reduce(
                              (acc, id) => acc + (catalog.find((c) => c.id === id)?.price || 0),
                              0,
                            ).toFixed(2)}{" "}
                            €
                          </span>{" "}
                          → ahorra{" "}
                          <span className="font-mono-data text-brand">
                            {Math.max(0,
                              (editing.course_ids || []).reduce(
                                (acc, id) => acc + (catalog.find((c) => c.id === id)?.price || 0),
                                0,
                              ) - Number(editing.fixed_price || 0),
                            ).toFixed(2)}{" "}
                            €
                          </span>
                        </p>
                      )}
                    </div>
                  </div>
                )}
              </div>
              <DialogFooter>
                <Button variant="ghost" onClick={() => setEditing(null)}>Cancelar</Button>
                <Button
                  onClick={savePromo}
                  disabled={savingPromo}
                  className="bg-brand hover:bg-brand-hover text-white"
                  data-testid="promo-save-btn"
                >
                  <Save className="w-4 h-4 mr-1" />
                  {savingPromo ? "Guardando…" : "Guardar promoción"}
                </Button>
              </DialogFooter>
            </>
          )}
        </DialogContent>
      </Dialog>

      <Dialog open={consentsOpen} onOpenChange={setConsentsOpen}>
        <DialogContent className="max-w-3xl max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Auditoría de consentimientos</DialogTitle>
          </DialogHeader>

          <div className="flex justify-end gap-2 mb-2">
            <Button
              variant="outline"
              size="sm"
              onClick={() => downloadConsents("csv")}
              data-testid="export-consents-csv"
            >
              <Upload className="w-3.5 h-3.5 mr-1 rotate-180" />
              Exportar CSV
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={() => downloadConsents("pdf")}
              data-testid="export-consents-pdf"
            >
              <Upload className="w-3.5 h-3.5 mr-1 rotate-180" />
              Exportar PDF
            </Button>
          </div>

          {consentsLoading ? (
            <p className="text-center text-gray-500 py-6 text-sm">Cargando…</p>
          ) : consents.length === 0 ? (
            <p className="text-center text-gray-500 py-6 text-sm">
              Aún no hay consentimientos registrados.
            </p>
          ) : (
            <div className="border border-gray-200 rounded-lg overflow-hidden">
              <table className="w-full text-sm">
                <thead className="bg-gray-50 border-b border-gray-200">
                  <tr className="text-left">
                    <th className="px-3 py-2 font-medium text-gray-600">Email</th>
                    <th className="px-3 py-2 font-medium text-gray-600">Fecha</th>
                    <th className="px-3 py-2 font-medium text-gray-600">Versión</th>
                    <th className="px-3 py-2 font-medium text-gray-600">IP</th>
                  </tr>
                </thead>
                <tbody>
                  {consents.map((c) => (
                    <tr key={c.id} className="border-b border-gray-100 last:border-b-0">
                      <td className="px-3 py-2 font-mono-data text-xs">{c.email}</td>
                      <td className="px-3 py-2 text-xs text-gray-600">
                        {new Date(c.accepted_at).toLocaleString("es-ES")}
                      </td>
                      <td className="px-3 py-2 font-mono-data text-xs">v{c.policy_version}</td>
                      <td className="px-3 py-2 font-mono-data text-xs text-gray-600">{c.ip || "—"}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
